import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeGrantStatus,
  createGrant,
  deleteGrant,
  getGrants,
  updateGrant,
} from '../api/grantsApi'
import type { Grant, GrantStatus, GrantUpsert, PagedResponse } from '../types'

const KEY = 'admin-grants'

/** Yetki (GRANTS) yönetimi sunucu-durumu: liste + oluştur/güncelle/durum/sil. */
export function useGrants() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY] })

  const applyUpdated = (updated: Grant) =>
    qc.setQueryData<PagedResponse<Grant>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((g) => (g.id === updated.id ? updated : g)) }
        : prev,
    )

  const list = useQuery({ queryKey: [KEY, i18n.language], queryFn: getGrants })

  // meta.handledCodes: bu domaine özel iş kodları ekranda ele alınır → merkezi hata toast'ı (019)
  // bu kodlarda susar (çift bildirim önlenir). Diğer hatalar (optimistic-lock/generic) merkezde.
  //
  // `create` 28.09.2026'dan beri ÇAĞIRANSIZDIR: "Yeni Yetki" butonu Yetki Yönetimi'nden kaldırıldı
  // (kullanıcı isteği) ve sayfa yalnız `update` kullanıyor. Yine de BİLİNÇLİ olarak duruyor —
  // silinseydi `grantsApi.createGrant` de ölü kalır, o da açık duran backend ucunu (`POST
  // /management/grants`) istemci tarafında tümüyle erişilemez bırakırdı. Hook bir veri erişim
  // katmanıdır, yüzey değil: kaldırılan yalnız butondu, yetenek değil.
  const create = useMutation({
    mutationFn: (body: GrantUpsert) => createGrant(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_CODE'] },
  })
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: GrantUpsert }) => updateGrant(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_CODE'] },
  })
  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: GrantStatus }) => changeGrantStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['GRANT_IN_USE'] },
  })
  const remove = useMutation({
    mutationFn: (id: number) => deleteGrant(id),
    onSuccess: invalidate,
    meta: { handledCodes: ['GRANT_IN_USE'] },
  })

  return { list, create, update, changeStatus, remove }
}
